import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {first} from 'rxjs/operators';
import {AuthenticationService} from '@services/authentification.service';
import {AlertService} from '@services/alert.service';
import {FormService} from '@services/form.service';
import {TrackingService} from '@services/tracking.service';
import {environment} from '~/environments/environment';
import {MatDialog} from '@angular/material/dialog';
import {Code2authComponent} from '@module/users/popup/code2auth/code2auth.component';
import {TOO_MANY_REQUESTS} from '~/app/shared/helpers/error.interceptor';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
    loginForm: UntypedFormGroup;

    loading = false;
    submitted = false;
    returnUrl: string;
    forgotMsg = '0';
    error = '';
    hide = true;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        public authenticationService: AuthenticationService,
        private alertService: AlertService,
        public formService: FormService,
        public dialog: MatDialog,
        private trackingService: TrackingService,
    ) {
        // redirect to home if already logged in
        if (this.authenticationService.isLogin) {
            this.router.navigate(['/app']);
        }
    }

    ngOnInit() {
        // get return url from route parameters or default to '/'
        this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/app';
        this.forgotMsg = this.route.snapshot.queryParams.forgot || '0';
        this.error = this.route.snapshot.queryParams.error || '';
        if (this.error == 'token') {
            setTimeout(function() {
                this.alertService.error('Un autre utilisateur s\'est connecté sur votre compte LyBox, vous avez été automatiquement déconnecté.\n Si vous n\'êtes pas à l\'origine de cette manoeuvre, nous vous conseillons de changer immédiatement votre mot de passe. En cas de problème, vous pouvez nous contacter à contact@lybox.fr', true);
            }.bind(this), 500);
        }

        // https://www.lybox.fr/app/login#state=%7B%22redirect%22:%22https://www.lybox.fr/app/search%22,%22provider%22:%22google%22%7D&access_token=ya29.a0Aa4xrXNbKW0UyWUM0vsfhkhY_bUNkvwWVSUT6vubAZYuqABsHuBtLnMOShwqzV-jmvsMmRxfkzKZFRYxCOXw0KW7KezC_a7BrSMALxTdjr01JpItAOFZPJig95PP-pKwGgD_BFZNmQknUEcgP3JnXgYjNNjXaCgYKATASARASFQEjDvL9J8hF829W4KDwPg1bsmcAZg0163&token_type=Bearer&expires_in=3599&scope=email%20profile%20https://www.googleapis.com/auth/userinfo.profile%20openid%20https://www.googleapis.com/auth/userinfo.email&authuser=1&hd=lydia-app.com&prompt=consent
        const url = window.location.href;
        if (url.indexOf('#') > 0) {
            const oauthParams = url.split('#');
            if (oauthParams.length === 2) {
                const query = Object();
                this.loading = true;
                const pairs = oauthParams[1].split('&');
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < pairs.length; i++) {
                    const pair = pairs[i].split('=');
                    query[decodeURIComponent(pair[0])] = '';
                    for (let i = 1; i < pair.length; i++) {
                        if (i > 1) {
                            query[decodeURIComponent(pair[0])] += '=';
                        }
                        query[decodeURIComponent(pair[0])] += decodeURIComponent(pair[i] || '');
                    }
                }
                if (query.access_token !== '') {
                    const json = JSON.parse(query.state.replace('\"', '"'));
                    if (json.redirect == 'null') {
                        json.redirect = '/app/search/result';
                    } else if (json.redirect == 'https://www.lybox.frextension') {
                        json.redirect =  "https://www.lybox.fr/extension-navigateur?action=login&";
                    }
                    if (json.provider === 'google') {
                        this.authenticationService.loginGoogle(query.access_token, json.code ? json.code : '', json.source ? json.source : '')
                            .pipe(first())
                            .subscribe(
                                isRegister => {
                                    if (isRegister) {
                                        this.trackingService.register('google');
                                    } else {
                                        this.trackingService.login('google');
                                    }
                                    window.location.href = json.redirect
                                },
                                (error) => this.loginError(error, true));
                    } else if (json.provider === 'facebook') {
                        this.authenticationService.loginFacebook(query.access_token, json.code ? json.code : '', json.source ? json.source : '')
                            .pipe(first())
                            .subscribe(
                                isRegister => {
                                    if (isRegister) {
                                        this.trackingService.register('facebook');
                                    } else {
                                        this.trackingService.login('facebook');
                                    }
                                    window.location.href = json.redirect;
                                },
                                (error) => this.loginError(error, true));
                    }
                }
            }
        }

        this.loginForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required]
        });
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.loginForm.controls;
    }

    openCode2auth() {
        const dialogRef = this.dialog.open(Code2authComponent, {
            width: '425px',
            height: '480px',
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result !== false && result !== undefined) {
                this.authenticationService.login(this.f.username.value, this.f.password.value, 'app', result)
                    .pipe(first())
                    .subscribe(
                        data => {
                            if (data.message && data.message == 'Success') {
                                this.trackingService.login('lybox');
                                this.authenticationService.loadUser().subscribe(() => {
                                    if (this.returnUrl == "extension") {
                                        window.location.href = "https://www.lybox.fr/extension-navigateur?action=login";
                                    } else {
                                        this.router.navigate([this.returnUrl.split('?')[0]]);
                                    }
                                });
                            }
                        },
                        (error) => {
                            this.alertService.error(error);
                            this.loading = false;
                        });
            } else {
                this.loading = false;
            }
        });
    }

    signInWithGoogle(): void {
        const location = 'https://accounts.google.com/o/oauth2/v2/auth?client_id=' + environment.google_client_id +
            '&redirect_uri=https://www.lybox.fr/app/login&response_type=token' +
            '&scope=https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile' +
            '&state={"redirect":"https://www.lybox.fr' + this.returnUrl + '","provider":"google","source":"app"}';
        window.location.href = location;
    }

    signInWithFB(): void {
        const location = 'https://www.facebook.com/v8.0/dialog/oauth?client_id=' + environment.facebook_client_id +
            '&redirect_uri=https://www.lybox.fr/app/login&response_type=token&scope=email%2Cpublic_profile' +
            '&state={"redirect":"https://www.lybox.fr' + this.returnUrl + '","provider":"facebook","source":"app42"}';
        console.log(location);
        window.location.href = location;
    }


    onSubmit() {
        this.submitted = true;
        this.forgotMsg = '0';

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return false;
        }

        this.loading = true;
        this.authenticationService.login(this.f.username.value, this.f.password.value, 'app')
            .pipe(first())
            .subscribe(
                data => {
                    if (data.message && data.message == 'Need2Auth') {
                        this.trackingService.need2auth('lybox');
                        this.openCode2auth();
                    } else if (data.message && data.message == 'Success') {
                        this.trackingService.login('lybox');
                        this.authenticationService.loadUser().subscribe(() => {
                            if (this.returnUrl == "extension") {
                                window.location.href = "https://www.lybox.fr/extension-navigateur?action=login";
                            } else {
                                this.router.navigate([this.returnUrl.split('?')[0]]);
                            }
                        });
                    }
                },
                (error) => this.loginError(error));
    }

    // Erreur de connexion (email/mot de passe, Google, Facebook)
    private loginError(error, social = false) {
        if (social && error === 'Not Found') {
            this.alertService.error('E-mail introuvable');
        } else if (error === 'Forbidden') {
            this.alertService.error('Email ou mot de passe incorrect');
        } else if (error === TOO_MANY_REQUESTS) {
            this.alertService.error('Trop de tentatives, réessayez dans quelques minutes.');
        } else {
            this.alertService.error(error);
        }
        this.loading = false;
    }
}
